import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import TextInput from 'components/Common/Form/Input/TextInput'
import IconButton from 'LuxKit/Button/IconButton'
import SolidMessageIcon from 'LuxKit/Icons/solid/SolidMessageIcon'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { addChatMessage } from 'actions/supportAssistant/supportAssistantActions'
import { EmptyArray, last } from 'lib/array/arrayUtils'
import { selectCurrentChat, selectOutageMessage } from 'supportAssistant/selectors/selectCurrentChat'
import { connect } from 'react-redux'
import { get, remove, set } from 'lib/storage/isomorphicSessionStorage'
import { trackClientEvent } from 'analytics/analytics'
import {
  CLEAR_SUPPORT_ASSISTANT_MESSAGE_DRAFT_EVENT,
  getSupportAssistantMessageDraftStorageKey,
} from 'supportAssistant/utils/messageDraftStorage'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'

interface MappedStateProps {
  currentChat: App.Chat | undefined
  outageMessage: string | undefined
}
interface Props {
  isTyping: boolean
  showDisclaimer?: boolean
}

const normalizeActionText = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9\s]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const isActionContent = (content: App.ChatMessageMetaContent) =>
  !!content.action && (content.type === 'button' || content.type === 'link')

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  width: 100%;
`

const DisclaimerText = styled(BodyText)`
  text-align: center;
`

function MessageInput(props: Props & MappedStateProps) {
  const { currentChat, outageMessage, isTyping, showDisclaimer = true } = props
  const intl = useIntl()

  const dispatch = useAppDispatch()

  const chatId = currentChat?.id ?? 'default'
  const messages: Array<App.ChatMessage> = useAppSelector(state => state.supportAssistant.messages[chatId] ?? EmptyArray)
  const [message, setMessage] = useState(() => get(getSupportAssistantMessageDraftStorageKey(chatId)) ?? '')

  const inputRef = useRef<HTMLInputElement>(null)
  const isMobile = useScreenSizeOnly('mobile')

  const lastMessage = last(messages)
  const isLastMessageByUser = lastMessage?.sender === 'user'
  const lastMessageMetaInputAction = lastMessage?.meta?.content?.find((meta) => meta.type === 'input')

  const validateInput = useCallback((msg: string, actionValidation?: { regex: string, message: string }) => {
    if (!actionValidation) {
      return msg.length < 250 ? '' : intl.formatMessage({
        id: 'UpcCSfioPOUOaA1T0Iat',
        defaultMessage: 'Messages are limited to 250 characters',
        description: 'Validation error when chat message exceeds character limit',
      })
    }

    return new RegExp(actionValidation.regex).test(msg) ? '' : actionValidation.message
  }, [intl])

  const validationMessage = validateInput(message, lastMessageMetaInputAction?.validation)
  const isMessageValid = validationMessage === ''

  const awaitingInitialAgentConnection = useMemo(() =>
    currentChat?.agentType === 'AGENT' &&
    (!currentChat?.agents?.length &&
      !messages.some((msg) => msg.sender === 'agent')
    ), [currentChat, messages])

  const isInputButtonDisabled = !lastMessageMetaInputAction && (currentChat?.status === 'CLOSED' || !message || isTyping || !!outageMessage || (currentChat?.agentType === 'BOT' && isLastMessageByUser)) || !isMessageValid
  const isInputDisabled = currentChat?.status === 'CLOSED' || awaitingInitialAgentConnection

  useEffect(() => {
    if (!message && !isMobile) {
      inputRef.current?.focus({
        preventScroll: true,
      })
    }
  }, [isMobile, message])

  useEffect(() => {
    setMessage(get(getSupportAssistantMessageDraftStorageKey(chatId)) ?? '')
  }, [chatId])

  const clearMessageDraft = useCallback(() => {
    setMessage('')
    remove(getSupportAssistantMessageDraftStorageKey(chatId))
  }, [chatId])

  useEffect(() => {
    window.addEventListener(CLEAR_SUPPORT_ASSISTANT_MESSAGE_DRAFT_EVENT, clearMessageDraft)

    return () => {
      window.removeEventListener(CLEAR_SUPPORT_ASSISTANT_MESSAGE_DRAFT_EVENT, clearMessageDraft)
    }
  }, [clearMessageDraft])

  const onMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newMessage = event.target.value
    setMessage(newMessage)

    if (newMessage) {
      set(getSupportAssistantMessageDraftStorageKey(chatId), newMessage)
    } else {
      remove(getSupportAssistantMessageDraftStorageKey(chatId))
    }
  }

  const clickMatchingVisibleAction = useCallback((messageText: string) => {
    if (lastMessage?.sender !== 'chatbot') {
      return false
    }

    const normalizedMessageText = normalizeActionText(messageText)
    if (!normalizedMessageText) {
      return false
    }

    const matchingAction = lastMessage.meta?.content
      ?.filter(isActionContent)
      .find(actionContent => normalizeActionText(actionContent.text ?? '') === normalizedMessageText)

    const matchingActionKey = matchingAction?.action?.key
    if (!matchingActionKey) {
      return false
    }

    const actionElements = Array.from(
      document.querySelectorAll<HTMLElement>('[data-support-assistant-action-key]'),
    )
    const matchingElements = actionElements.filter(element =>
      element.dataset.supportAssistantActionKey === matchingActionKey &&
      normalizeActionText(element.textContent ?? '') === normalizeActionText(matchingAction.text ?? '') &&
      element.getClientRects().length > 0,
    )
    const matchingElement = matchingElements[matchingElements.length - 1]

    if (!matchingElement) {
      return false
    }

    trackClientEvent({
      subject: 'support_assistant',
      action: `lesa-text-exact-match-pill-clicked-${matchingActionKey}`,
      category: 'logging',
      type: 'interaction',
    })

    matchingElement.click()
    return true
  }, [lastMessage])

  const handleMessageSubmit = useCallback(() => {
    if (!lastMessageMetaInputAction && clickMatchingVisibleAction(message)) {
      clearMessageDraft()
      return
    }

    // we need to attach the action of the last message to the payload.
    // This is so the back end understands the action to be done when free text is entered.
    const messageMeta: App.ChatMessageMeta = lastMessageMetaInputAction ?
        {
          action: lastMessageMetaInputAction.action?.key,
          payload: { ...lastMessage?.meta?.payload, inputPayload: message },
        } : {
          payload: lastMessage?.meta?.payload,
        }

    dispatch(addChatMessage(message, messageMeta))
    clearMessageDraft()
  }, [dispatch, lastMessage?.meta?.payload, lastMessageMetaInputAction, message, clickMatchingVisibleAction, clearMessageDraft])

  const onKeyEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const isEnter = event.type === 'keyup' && event.key === 'Enter'

    if (isEnter && !isInputButtonDisabled) {
      handleMessageSubmit()
    }
  }

  const onMessageSubmit = () => {
    handleMessageSubmit()
  }

  const agentConnectionPlaceholder = intl.formatMessage({
    id: 'VYj6NB_B2xoxtuDx__OO',
    defaultMessage: 'We are connecting you with an agent...',
    description: 'Placeholder text shown while waiting for agent connection',
  })

  const placeholder = lastMessageMetaInputAction ? lastMessageMetaInputAction?.text : intl.formatMessage({
    id: 'JNFCAq_talUJ3kVzsyEy',
    defaultMessage: 'Type a message',
    description: 'Default placeholder text for chat message input on the form',
  })

  const disclaimerText = intl.formatMessage({
    id: 'q-F8A0Y4I-7VoSI8pcr6',
    defaultMessage: 'LESA AI can make mistakes. Double-check important information.',
    description: 'AI response disclaimer text displayed below the LESA support assistant chat input',
  })

  return (
    <InputContainer>
      <TextInput
        type="text"
        maxLength={250}
        disabled={isInputDisabled}
        name="Message"
        aria-label={intl.formatMessage({
          id: 'u9gyjMYjfEp22sSXGe1Q',
          defaultMessage: 'Message',
          description: 'Accessibility aria-label for chat message input field',
        })}
        autoComplete="off"
        placeholder={awaitingInitialAgentConnection ? agentConnectionPlaceholder : placeholder}
        onChange={onMessageChange}
        value={message}
        ref={inputRef}
        onKeyUp={onKeyEnter}
        invalidErrorMessage={outageMessage}
        manualError
        noValidationSpacing
        helpText={validationMessage}
        endIcon={<IconButton
          kind={lastMessageMetaInputAction ? 'primary' : 'tertiary'}
          variant="dark"
          shape="circle"
          horizontalOutdent="end"
          onClick={onMessageSubmit}
          disabled={isInputButtonDisabled}
        >
          <SolidMessageIcon />
        </IconButton>}
      />
      {showDisclaimer && (
        <DisclaimerText variant="small" colour="light-foreground-alt">
          {disclaimerText}
        </DisclaimerText>
      )}
    </InputContainer>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>((state) => {
  return {
    currentChat: selectCurrentChat(state),
    outageMessage: selectOutageMessage(state),
  }
})(MessageInput)
